<template>
	<v-container fluid class="pa-4">
		<WaitDialog :isActive="isWaiting" title="Executing telemetry operations..." />

		<!-- Header Section -->
		<v-row class="mb-4">
			<v-col cols="12">
				<v-card class="pc">
					<v-card-title class="text-h4">
						<v-icon icon="mdi-chart-line" class="mr-2" />
						Telemetry Operations Test Guide
					</v-card-title>
					<v-card-text>
						<p class="text-body-1">
							Test and validate all Azure Application Insights telemetry operations. Each card
							below
							represents a telemetry method from the TelemetryContract interface.
						</p>
						<p class="text-body-2 text-medium-emphasis mt-2">
							<v-icon icon="mdi-information" size="small" class="mr-1" />
							Open browser DevTools Console to see detailed execution logs and check Azure
							Application
							Insights to verify telemetry data.
						</p>
					</v-card-text>
					<v-card-actions>
						<v-spacer />
						<m-btn color="primary" prepend-icon="mdi-play-circle" text="Execute All Operations"
							@click="executeAllOperations" :loading="isExecutingAll" />
						<m-btn color="secondary" prepend-icon="mdi-refresh" text="Clear Console"
							@click="clearConsole" />
						<m-btn prepend-icon="mdi-broom" text="Flush Telemetry" @click="flushTelemetry" />
					</v-card-actions>
				</v-card>
			</v-col>
		</v-row>

		<!-- Telemetry Operations Grid -->
		<v-row dense>
			<v-col v-for="op in telemetryOperations" :key="op.name" cols="12" md="6" lg="4">
				<v-card :class="op.executed ? 'tc' : ''" height="100%">
					<v-card-title class="text-h6">
						<v-icon :icon="op.icon" class="mr-2" />
						{{ op.title }}
						<v-spacer />
						<v-chip v-if="op.executed" color="success" size="small" prepend-icon="mdi-check">
							Executed
						</v-chip>
					</v-card-title>
					<v-card-text>
						<p class="text-body-2">{{ op.description }}</p>
						<v-divider class="my-2" />
						<p class="text-caption text-medium-emphasis">
							<strong>Method:</strong> <code>{{ op.method }}</code>
						</p>
						<p v-if="op.severity" class="text-caption text-medium-emphasis">
							<strong>Severity:</strong> {{ op.severity }}
						</p>

						<!-- Results Section -->
						<v-divider v-if="op.results && op.results.length > 0" class="my-2" />
						<div v-if="op.results && op.results.length > 0" class="results-container">
							<div v-for="(result, index) in op.results" :key="index" class="result-item"
								:class="result.success ? 'text-success' : 'text-error'">
								<v-icon :icon="result.success ? 'mdi-check-circle' : 'mdi-alert-circle'"
									:color="result.success ? 'success' : 'error'" size="small" class="mr-1" />
								<span class="text-caption">
									<strong>{{ result.timestamp }}:</strong> {{ result.message }}
								</span>
							</div>
						</div>
					</v-card-text>
					<v-card-actions>
						<m-btn v-if="op.results && op.results.length > 0" text="Clear" size="small"
							@click="op.results = []" />
						<v-spacer />
						<m-btn color="primary" @click="op.execute" :loading="op.loading">
							Execute Now
						</m-btn>
					</v-card-actions>
				</v-card>
			</v-col>
		</v-row>

		<!-- Additional Information -->
		<v-row class="mt-4">
			<v-col cols="12">
				<v-expansion-panels>
					<v-expansion-panel>
						<v-expansion-panel-title>
							<v-icon icon="mdi-information-outline" class="mr-2" />
							About Telemetry Operations
						</v-expansion-panel-title>
						<v-expansion-panel-text>
							<h3 class="text-h6 mb-2">Telemetry Contract Methods</h3>
							<ul class="text-body-2">
								<li><strong>trackEvent:</strong> Track custom application events like button
									clicks or
									user actions</li>
								<li><strong>trackTrace:</strong> Log diagnostic traces with different
									severity levels
								</li>
								<li><strong>trackException:</strong> Record errors and exceptions with
									context</li>
								<li><strong>trackPageView:</strong> Track page navigation and views</li>
								<li><strong>trackMetric:</strong> Record custom performance metrics</li>
								<li><strong>startTrackEvent/stopTrackEvent:</strong> Measure operation
									duration</li>
								<li><strong>flush:</strong> Force immediate send of all buffered telemetry
								</li>
							</ul>
							<h3 class="text-h6 mt-4 mb-2">Severity Levels</h3>
							<ul class="text-body-2">
								<li><v-chip size="x-small" class="mr-1">Verbose</v-chip> Detailed diagnostic
									information</li>
								<li><v-chip size="x-small" class="mr-1">Information</v-chip> General
									informational
									messages</li>
								<li><v-chip size="x-small" class="mr-1">Warning</v-chip> Warning messages
									for
									potentially harmful situations</li>
								<li><v-chip size="x-small" class="mr-1">Error</v-chip> Error events that
									might still
									allow the application to continue</li>
								<li><v-chip size="x-small" class="mr-1">Critical</v-chip> Critical failures
									that
									require immediate attention</li>
							</ul>
						</v-expansion-panel-text>
					</v-expansion-panel>
				</v-expansion-panels>
			</v-col>
		</v-row>
	</v-container>
</template>

<script lang="ts">
import { ref, inject, Ref } from 'vue';
import { TelemetryContract } from '@/contracts/TelemetryContract';
import WaitDialog from '@/components/WaitDialog.vue';
import BaseButton from '@/components/BaseButton.vue';

interface TelemetryOperation {
	name: string;
	title: string;
	description: string;
	method: string;
	icon: string;
	severity?: string;
	execute: () => Promise<void>;
	loading: boolean;
	executed: boolean;
	results: ExecutionResult[];
}

interface ExecutionResult {
	operation: string;
	success: boolean;
	message: string;
	timestamp: string;
}

export default {
	name: 'TelemetryGuide',
	route: {
		path: '/telemetry-guide',
		meta: {
			requiresAuth: false,
			title: 'Telemetry Testing Guide'
		}
	},
	components: {
		WaitDialog,
		'm-btn': BaseButton,
	},
	setup() {
		const telemetry = inject<TelemetryContract>('telemetry');
		const isWaiting = ref(false);
		const isExecutingAll = ref(false);

		const addResult = (operationName: string, success: boolean, message: string) => {
			const op = telemetryOperations.value.find(o => o.name === operationName);
			if (op) {
				const now = new Date();
				const timestamp = now.toLocaleTimeString('en-US', { hour12: false });
				op.results.push({
					operation: op.title,
					success,
					message,
					timestamp
				});
			}
		};

		// Define all telemetry operations
		const telemetryOperations = ref<TelemetryOperation[]>([
			{
				name: 'trackEvent',
				title: 'Track Event',
				description: 'Track a custom application event with properties. Useful for user interactions and business events.',
				method: 'trackEvent(event: IEventTelemetry)',
				icon: 'mdi-calendar-star',
				loading: false,
				executed: false, results: [], execute: async () => {
					const op = telemetryOperations.value.find(o => o.name === 'trackEvent');
					if (!op) return;
					op.loading = true;
					try {
						console.log('🔵 [Telemetry Test] Executing trackEvent...');
						telemetry?.trackEvent({
							name: 'TelemetryGuide_TestEvent',
							properties: {
								testType: 'manual',
								component: 'TelemetryGuide',
								timestamp: new Date().toISOString(),
								testData: 'Sample event data',
								userId: 'test-user-123',
								sessionId: crypto.randomUUID(),
							},
						});
						console.log('✅ trackEvent executed successfully');
						addResult('trackEvent', true, 'Event tracked with custom properties');
						op.executed = true;
					} catch (error) {
						console.error('❌ trackEvent failed:', error);
						addResult('trackEvent', false, `Failed: ${error}`);
					} finally {
						op.loading = false;
					}
				},
			},
			{
				name: 'trackTrace_Verbose',
				title: 'Track Trace (Verbose)',
				description: 'Log a verbose diagnostic trace. Use for detailed debugging information.',
				method: 'trackTrace(trace: ITraceTelemetry)',
				icon: 'mdi-text',
				severity: 'Verbose',
				loading: false,
				executed: false, results: [], execute: async () => {
					const op = telemetryOperations.value.find(o => o.name === 'trackTrace_Verbose');
					if (!op) return;
					op.loading = true;
					try {
						console.log('🔵 [Telemetry Test] Executing trackTrace (Verbose)...');
						telemetry?.trackTrace({
							message: 'TelemetryGuide: Verbose level trace for detailed debugging',
							severityLevel: telemetry?.severityLevel.Verbose,
							properties: {
								component: 'TelemetryGuide',
								traceLevel: 'Verbose',
								details: 'This is a very detailed log entry for debugging purposes',
							},
						});
						console.log('✅ trackTrace (Verbose) executed successfully');
						addResult('trackTrace_Verbose', true, 'Verbose trace logged successfully');
						op.executed = true;
					} catch (error) {
						console.error('❌ trackTrace (Verbose) failed:', error);
						addResult('trackTrace_Verbose', false, `Failed: ${error}`);
					} finally {
						op.loading = false;
					}
				},
			},
			{
				name: 'trackTrace_Information',
				title: 'Track Trace (Information)',
				description: 'Log an informational trace. Use for general application flow information.',
				method: 'trackTrace(trace: ITraceTelemetry)',
				icon: 'mdi-information',
				severity: 'Information',
				loading: false,
				executed: false, results: [], execute: async () => {
					const op = telemetryOperations.value.find(o => o.name === 'trackTrace_Information');
					if (!op) return;
					op.loading = true;
					try {
						console.log('🔵 [Telemetry Test] Executing trackTrace (Information)...');
						telemetry?.trackTrace({
							message: 'TelemetryGuide: Informational trace about application state',
							severityLevel: telemetry?.severityLevel.Information,
							properties: {
								component: 'TelemetryGuide',
								traceLevel: 'Information',
								applicationState: 'running',
								userActivity: 'testing telemetry',
							},
						});
						console.log('✅ trackTrace (Information) executed successfully');
						addResult('trackTrace_Information', true, 'Information trace logged successfully');
						op.executed = true;
					} catch (error) {
						console.error('❌ trackTrace (Information) failed:', error);
						addResult('trackTrace_Information', false, `Failed: ${error}`);
					} finally {
						op.loading = false;
					}
				},
			},
			{
				name: 'trackTrace_Warning',
				title: 'Track Trace (Warning)',
				description: 'Log a warning trace. Use for potentially harmful situations that need attention.',
				method: 'trackTrace(trace: ITraceTelemetry)',
				icon: 'mdi-alert',
				severity: 'Warning',
				loading: false,
				executed: false, results: [], execute: async () => {
					const op = telemetryOperations.value.find(o => o.name === 'trackTrace_Warning');
					if (!op) return;
					op.loading = true;
					try {
						console.log('🔵 [Telemetry Test] Executing trackTrace (Warning)...');
						telemetry?.trackTrace({
							message: 'TelemetryGuide: Warning - potential issue detected in test scenario',
							severityLevel: telemetry?.severityLevel.Warning,
							properties: {
								component: 'TelemetryGuide',
								traceLevel: 'Warning',
								potentialIssue: 'Test warning scenario',
								recommendedAction: 'Monitor this area',
							},
						});
						console.log('⚠️ trackTrace (Warning) executed successfully');
						addResult('trackTrace_Warning', true, 'Warning trace logged successfully');
						op.executed = true;
					} catch (error) {
						console.error('❌ trackTrace (Warning) failed:', error);
						addResult('trackTrace_Warning', false, `Failed: ${error}`);
					} finally {
						op.loading = false;
					}
				},
			},
			{
				name: 'trackTrace_Error',
				title: 'Track Trace (Error)',
				description: 'Log an error trace. Use for error conditions that the application can recover from.',
				method: 'trackTrace(trace: ITraceTelemetry)',
				icon: 'mdi-alert-circle',
				severity: 'Error',
				loading: false,
				executed: false, results: [], execute: async () => {
					const op = telemetryOperations.value.find(o => o.name === 'trackTrace_Error');
					if (!op) return;
					op.loading = true;
					try {
						console.log('🔵 [Telemetry Test] Executing trackTrace (Error)...');
						telemetry?.trackTrace({
							message: 'TelemetryGuide: Error trace for recoverable error condition',
							severityLevel: telemetry?.severityLevel.Error,
							properties: {
								component: 'TelemetryGuide',
								traceLevel: 'Error',
								errorScenario: 'Simulated recoverable error',
								recoveryAction: 'Error was handled gracefully',
							},
						});
						console.log('❌ trackTrace (Error) executed successfully');
						addResult('trackTrace_Error', true, 'Error trace logged successfully');
						op.executed = true;
					} catch (error) {
						console.error('❌ trackTrace (Error) failed:', error);
						addResult('trackTrace_Error', false, `Failed: ${error}`);
					} finally {
						op.loading = false;
					}
				},
			},
			{
				name: 'trackTrace_Critical',
				title: 'Track Trace (Critical)',
				description: 'Log a critical trace. Use for severe failures requiring immediate attention.',
				method: 'trackTrace(trace: ITraceTelemetry)',
				icon: 'mdi-alert-octagon',
				severity: 'Critical',
				loading: false,
				executed: false, results: [], execute: async () => {
					const op = telemetryOperations.value.find(o => o.name === 'trackTrace_Critical');
					if (!op) return;
					op.loading = true;
					try {
						console.log('🔵 [Telemetry Test] Executing trackTrace (Critical)...');
						telemetry?.trackTrace({
							message: 'TelemetryGuide: Critical trace for severe system failure (TEST ONLY)',
							severityLevel: telemetry?.severityLevel.Critical,
							properties: {
								component: 'TelemetryGuide',
								traceLevel: 'Critical',
								criticalScenario: 'Simulated critical system failure',
								impact: 'TEST - No actual critical failure',
							},
						});
						console.log('🔴 trackTrace (Critical) executed successfully');
						addResult('trackTrace_Critical', true, 'Critical trace logged successfully');
						op.executed = true;
					} catch (error) {
						console.error('❌ trackTrace (Critical) failed:', error);
						addResult('trackTrace_Critical', false, `Failed: ${error}`);
					} finally {
						op.loading = false;
					}
				},
			},
			{
				name: 'trackException_Error',
				title: 'Track Exception (Error)',
				description: 'Track an exception with Error object. Includes stack trace and custom properties.',
				method: 'trackException(error: Error, severityLevel, properties, measurements)',
				icon: 'mdi-bug',
				severity: 'Error',
				loading: false,
				executed: false, results: [], execute: async () => {
					const op = telemetryOperations.value.find(o => o.name === 'trackException_Error');
					if (!op) return;
					op.loading = true;
					try {
						console.log('🔵 [Telemetry Test] Executing trackException (Error)...');
						const testError = new Error('TelemetryGuide: Test exception from telemetry guide');
						testError.name = 'TestException';
						telemetry?.trackException(
							testError,
							telemetry?.severityLevel.Error,
							{
								component: 'TelemetryGuide',
								testScenario: 'exception tracking',
								errorContext: 'Simulated error for testing',
								userAction: 'Clicked Execute Exception Test',
							},
							{
								testMetric: 42,
								attemptNumber: 1,
							}
						);
						console.log('✅ trackException (Error) executed successfully');
						addResult('trackException_Error', true, 'Exception tracked with Error object');
						op.executed = true;
					} catch (error) {
						console.error('❌ trackException (Error) failed:', error);
						addResult('trackException_Error', false, `Failed: ${error}`);
					} finally {
						op.loading = false;
					}
				},
			},
			{
				name: 'trackException_IExceptionTelemetry',
				title: 'Track Exception (Telemetry)',
				description: 'Track an exception using IExceptionTelemetry interface with custom properties.',
				method: 'trackException(error: IExceptionTelemetry)',
				icon: 'mdi-alert-box',
				severity: 'Warning',
				loading: false,
				executed: false, results: [], execute: async () => {
					const op = telemetryOperations.value.find(o => o.name === 'trackException_IExceptionTelemetry');
					if (!op) return;
					op.loading = true;
					try {
						console.log('🔵 [Telemetry Test] Executing trackException (IExceptionTelemetry)...');
						telemetry?.trackException({
							exception: new Error('TelemetryGuide: IExceptionTelemetry test error'),
							severityLevel: telemetry?.severityLevel.Warning,
							properties: {
								testType: 'IExceptionTelemetry',
								component: 'TelemetryGuide',
								handledBy: 'TelemetryGuide test function',
							},
						});
						console.log('✅ trackException (IExceptionTelemetry) executed successfully');
						addResult('trackException_IExceptionTelemetry', true, 'Exception tracked with IExceptionTelemetry');
						op.executed = true;
					} catch (error) {
						console.error('❌ trackException (IExceptionTelemetry) failed:', error);
						addResult('trackException_IExceptionTelemetry', false, `Failed: ${error}`);
					} finally {
						op.loading = false;
					}
				},
			},
			{
				name: 'trackPageView',
				title: 'Track Page View',
				description: 'Track a page view event. Useful for SPA navigation tracking.',
				method: 'trackPageView(name?: string, uri?: string)',
				icon: 'mdi-eye',
				loading: false,
				executed: false, results: [], execute: async () => {
					const op = telemetryOperations.value.find(o => o.name === 'trackPageView');
					if (!op) return;
					op.loading = true;
					try {
						console.log('🔵 [Telemetry Test] Executing trackPageView...');
						telemetry?.trackPageView(
							'TelemetryGuide_TestPageView',
							'/telemetry-guide/test'
						);
						console.log('✅ trackPageView executed successfully');
						addResult('trackPageView', true, 'Page view tracked successfully');
						op.executed = true;
					} catch (error) {
						console.error('❌ trackPageView failed:', error);
						addResult('trackPageView', false, `Failed: ${error}`);
					} finally {
						op.loading = false;
					}
				},
			},
			{
				name: 'trackMetric',
				title: 'Track Metric',
				description: 'Track a custom performance metric. Useful for business or technical KPIs.',
				method: 'trackMetric(name: string, average: number, properties)',
				icon: 'mdi-chart-bar',
				loading: false,
				executed: false, results: [], execute: async () => {
					const op = telemetryOperations.value.find(o => o.name === 'trackMetric');
					if (!op) return;
					op.loading = true;
					try {
						console.log('🔵 [Telemetry Test] Executing trackMetric...');
						telemetry?.trackMetric(
							'TelemetryGuide_TestMetric_ResponseTime',
							245.67,
							{
								metricType: 'performance',
								component: 'TelemetryGuide',
								unit: 'milliseconds',
								operation: 'test_operation',
								testRun: 'true',
							}
						);
						console.log('✅ trackMetric executed successfully');
						addResult('trackMetric', true, 'Metric tracked: 245.67ms response time');
						op.executed = true;
					} catch (error) {
						console.error('❌ trackMetric failed:', error);
						addResult('trackMetric', false, `Failed: ${error}`);
					} finally {
						op.loading = false;
					}
				},
			},
			{
				name: 'startTrackEvent',
				title: 'Start Track Event',
				description: 'Start timing a custom event. SDK uses event name for correlation. Use with stopTrackEvent to measure operation duration.',
				method: 'startTrackEvent(name: string)',
				icon: 'mdi-timer-play',
				loading: false,
				executed: false, results: [], execute: async () => {
					const op = telemetryOperations.value.find(o => o.name === 'startTrackEvent');
					if (!op) return;
					op.loading = true;
					try {
						console.log('🔵 [Telemetry Test] Executing startTrackEvent...');
						telemetry?.startTrackEvent('TelemetryGuide_TestTimedOperation');
						console.log('✅ startTrackEvent executed successfully (event name: TelemetryGuide_TestTimedOperation)');
						addResult('startTrackEvent', true, 'Timed event started (SDK correlates by event name)');
						op.executed = true;
					} catch (error) {
						console.error('❌ startTrackEvent failed:', error);
						addResult('startTrackEvent', false, `Failed: ${error}`);
					} finally {
						op.loading = false;
					}
				},
			},
			{
				name: 'stopTrackEvent',
				title: 'Stop Track Event',
				description: 'Stop timing a custom event (must match event name from startTrackEvent) and record the duration with properties and measurements.',
				method: 'stopTrackEvent(name: string, properties, measurements)',
				icon: 'mdi-timer-stop',
				loading: false,
				executed: false, results: [], execute: async () => {
					const op = telemetryOperations.value.find(o => o.name === 'stopTrackEvent');
					if (!op) return;
					op.loading = true;
					try {
						console.log('🔵 [Telemetry Test] Executing stopTrackEvent...');
						const startOp = telemetryOperations.value.find(o => o.name === 'startTrackEvent');
						if (!startOp?.executed) {
							console.warn('⚠️ startTrackEvent not executed yet. Starting timer first...');
							telemetry?.startTrackEvent('TelemetryGuide_TestTimedOperation');
							await new Promise(resolve => setTimeout(resolve, 500));
						}
						telemetry?.stopTrackEvent('TelemetryGuide_TestTimedOperation', {
							operationType: 'test',
							component: 'TelemetryGuide',
							completed: 'true',
						}, {
							itemsProcessed: 42,
							duration: 500,
						});
						console.log('✅ stopTrackEvent executed successfully');
						addResult('stopTrackEvent', true, 'Timed event stopped and duration recorded (correlated by event name)');
						op.executed = true;
					} catch (error) {
						console.error('❌ stopTrackEvent failed:', error);
						addResult('stopTrackEvent', false, `Failed: ${error}`);
					} finally {
						op.loading = false;
					}
				},
			},
			{
				name: 'flush',
				title: 'Flush Telemetry',
				description: 'Force immediate transmission of all buffered telemetry data to Application Insights.',
				method: 'flush()',
				icon: 'mdi-upload',
				loading: false,
				executed: false, results: [], execute: async () => {
					const op = telemetryOperations.value.find(o => o.name === 'flush');
					if (!op) return;
					op.loading = true;
					try {
						console.log('🔵 [Telemetry Test] Executing flush...');
						telemetry?.flush();
						console.log('✅ flush executed successfully - telemetry data sent to Application Insights');
						addResult('flush', true, 'All telemetry data flushed to Application Insights');
						op.executed = true;
					} catch (error) {
						console.error('❌ flush failed:', error);
						addResult('flush', false, `Failed: ${error}`);
					} finally {
						op.loading = false;
					}
				},
			},
		]);

		const executeAllOperations = async () => {
			console.log('=== Starting Comprehensive Telemetry Test Suite ===');
			isExecutingAll.value = true;
			isWaiting.value = true;

			for (const op of telemetryOperations.value) {
				try {
					await op.execute();
					// Add small delay between operations for clarity
					await new Promise(resolve => setTimeout(resolve, 100));
				} catch (error) {
					console.error(`Failed to execute ${op.name}:`, error);
				}
			}

			isWaiting.value = false;
			isExecutingAll.value = false;
			console.log('=== Telemetry Test Suite Completed ===');
			addResult('executeAllOperations', true, 'All telemetry operations executed successfully');
		};

		const flushTelemetry = () => {
			try {
				telemetry?.flush();
				console.log('✅ Telemetry flushed manually');
				addResult('flush', true, 'Telemetry data sent to Application Insights');
			} catch (error) {
				console.error('❌ Manual flush failed:', error);
				addResult('flush', false, `Failed: ${error}`);
			}
		};

		const clearConsole = () => {
			console.clear();
			console.log('Console cleared - ready for new telemetry tests');
		};

		return {
			telemetryOperations,
			executeAllOperations,
			flushTelemetry,
			clearConsole,
			isWaiting,
			isExecutingAll,
		};
	}
};
</script>

<style scoped>
.results-container {
	margin-top: 8px;
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.result-item {
	display: flex;
	align-items: center;
	padding: 4px 0;
}

.v-card {
	transition: all 0.3s ease;
}

.v-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

code {
	background-color: rgba(0, 0, 0, 0.05);
	padding: 2px 6px;
	border-radius: 3px;
	font-family: 'Courier New', monospace;
	font-size: 0.875rem;
}
</style>
